import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip';
import { InfoCircleIcon } from '@orn/shared-web/icons';

import { formatAccountSubtype } from '../../../../utils/account/accounts';
import { formatCalendarDate, toCurrency } from '../../../../utils/formatting';
import Entity from '../../../entity';
import { TRANSACTION_PAIR_TOOLTIP } from '../../../records/rows';

import type { FinancialAccountSubtype } from '../../../../__generated__/globalTypes';
import type { CalendarDate } from '@internationalized/date';

interface ITransactionPair {
  id: string;
  date: CalendarDate;
  amount: string;
  financialAccount: {
    id: string;
    subtype: FinancialAccountSubtype;
    mask: string;
    name: string;
    bank?: {
      id?: string;
      name?: string;
      logoBase64Encoding?: string | null;
      logoUrl?: string | null;
    } | null;
  };
}

const TransactionPairTooltip = ({ children, pair }: { children: React.ReactNode; pair: ITransactionPair }) => {
  const amountNumber = Number(pair.amount);
  const isCredit = amountNumber < 0;
  const amount = toCurrency(Math.abs(amountNumber));
  const formattedAmount = isCredit ? `+${amount}` : `-${amount}`;

  return (
    <Tooltip
      title={
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Entity.Avatar entity={pair.financialAccount} size="sm" />
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-medium text-white">{pair.financialAccount.name}</span>
              <span className="text-quaternary truncate text-xs font-medium">
                {formatCalendarDate(pair.date, 'short')} | {formatAccountSubtype(pair.financialAccount.subtype)} ••
                {pair.financialAccount.mask}
              </span>
            </div>
          </div>
          <div className={'flex gap-2 self-start text-sm font-medium'}>{formattedAmount}</div>
        </div>
      }
      description={
        <div className="border-gray-light-700 mt-2 w-full border-t pt-1.5">
          <span className="text-tertiary inline w-full min-w-0 items-center gap-1.5 text-xs font-medium italic">
            <InfoCircleIcon className="size-3.25 mb-0.5 mr-0.5 inline shrink-0" />
            {TRANSACTION_PAIR_TOOLTIP}
          </span>
        </div>
      }
    >
      <TooltipTrigger>{children}</TooltipTrigger>
    </Tooltip>
  );
};

export default TransactionPairTooltip;
